import React from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CloseRounded, CalendarMonthOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import EarningsSessionColumn from "./EarningsSessionColumn";
import { splitDay } from "./earningsCalendarSelection";
import { EarningsCalendarDay } from "../../../hooks/useEarningsCalendar";
import { panelBorder, panelHairline, overlaySurface } from "../overviewPanel";

dayjs.extend(utc);

// The full day, split the same way the grid splits it — before open on the
// left, after close on the right — so the modal reads as the same object,
// just complete. The grid's door is trimmed to the biggest handful; "view all"
// means ALL.

interface EarningsDayModalProps {
  day: EarningsCalendarDay | null;
  open: boolean;
  onClose: () => void;
}

const EarningsDayModal: React.FC<EarningsDayModalProps> = ({
  day,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const split = day ? splitDay(day.events) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth="md"
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" } },
        paper: {
          sx: {
            backgroundColor: overlaySurface(isDark),
            backgroundImage: "none",
            border: isPhone ? "none" : `1px solid ${panelBorder(isDark)}`,
            borderRadius: isPhone ? 0 : 2,
            boxShadow: "none",
            maxHeight: isPhone ? "100%" : "min(760px, calc(100dvh - 64px))",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.5,
          pt: isPhone ? "max(env(safe-area-inset-top), 16px)" : 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${panelHairline(isDark)}`,
        }}
      >
        <CalendarMonthOutlined sx={{ fontSize: 16, color: "#22c55e" }} />
        <Typography
          sx={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {day && dayjs.utc(day.date).format("dddd D MMMM")}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.05)",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
          }}
        >
          {day?.events.length ?? 0} reporting
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ width: 36, height: 36 }}
        >
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          pb: isPhone ? "max(env(safe-area-inset-bottom), 20px)" : 2.5,
          overflowY: "auto",
          // A finger scrolls without one; a pointer wants the bar on a list
          // this long.
          "@media (hover: none)": {
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
        }}
      >
        {split && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1fr) minmax(0, 1fr)",
              },
              gap: { xs: 2, sm: 2.5 },
              alignItems: "start",
            }}
          >
            <EarningsSessionColumn session={split.bmo} />
            <EarningsSessionColumn session={split.amc} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default EarningsDayModal;
